import { Patient } from '../types/patient';
import jacobFentonSuggestionsData from '../data_store/jacob_fenton_suggestions.json';
import { CLEVELAND_HEARTLAB_RANGES } from '../config/referenceRanges';

// Type definitions for comprehensive patient data
interface ComprehensivePatientData {
  person_id: string;
  metadata: {
    source_file: string;
    parsed_at: string;
    total_encounters: number;
  };
  encounters: Array<{
    person: {
      birthdate: string;
      age: { days: number; years: number };
      demographics: {
        ethnicity: string;
        gender: string;
        birthdate: string;
      };
    };
    entries: Array<{
      timestamp: string;
      events: Array<{
        type: string;
        code: string;
        name: string;
        value?: string;
        unit?: string;
        visit_id?: string;
        note_id?: string;
      }>;
    }>;
  }>;
}

/**
 * Validate filename format: firstname_lastname.xml or firstname_lastname.pdf
 */
export function isValidFilenameFormat(filename: string): boolean {
  // Remove file extension and check format
  const nameWithoutExt = filename.replace(/\.(xml|pdf)$/i, '');

  // Check if it matches firstname_lastname pattern (at least one underscore)
  const pattern = /^[a-z]+_[a-z]+$/i;
  return pattern.test(nameWithoutExt);
}

/**
 * Extract patient name from filename (firstname_lastname.xml -> {firstName, lastName})
 */
export function extractPatientNameFromFilename(filename: string): { firstName: string; lastName: string } | null {
  if (!isValidFilenameFormat(filename)) {
    return null;
  }

  // Remove extension
  const nameWithoutExt = filename.replace(/\.(xml|pdf)$/i, '');

  // Split by underscore
  const parts = nameWithoutExt.split('_');

  if (parts.length !== 2) {
    return null;
  }

  // Capitalize first letter of each name
  const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

  return {
    firstName: capitalize(parts[0]),
    lastName: capitalize(parts[1])
  };
}

/**
 * Helper to detect if a filename matches our pre-loaded patients
 */
export function isPreloadedPatient(filename: string): boolean {
  const normalizedName = filename.toLowerCase().replace(/\s+/g, '_');
  return normalizedName === 'barbara_smith.xml' ||
         normalizedName === 'jacob_fenton.xml' ||
         normalizedName === 'john_smith.xml';
}

/**
 * Extract patient identifier from filename
 */
export function getPatientIdentifier(filename: string): string | null {
  const normalizedName = filename.toLowerCase().replace(/\s+/g, '_');
  if (normalizedName === 'barbara_smith.xml') return 'barbara_smith';
  if (normalizedName === 'jacob_fenton.xml') return 'jacob_fenton';
  if (normalizedName === 'john_smith.xml') return 'john_smith';
  return null;
}

/**
 * Load supplemental JSON data for a patient from public/data_store
 */
export async function loadSupplementalData(
  patientId: string,
  onProgress?: (progress: number) => void
) {
  const basePath = `/data_store/${patientId}`;

  const loadJSON = async (filename: string) => {
    try {
      const response = await fetch(`${basePath}${filename}`);
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      console.warn(`Failed to load ${basePath}${filename}:`, error);
      return null;
    }
  };

  // Use imported suggestions for jacob_fenton (from src/data_store) to get correct data
  let suggestions = null;
  if (patientId === 'jacob_fenton') {
    suggestions = jacobFentonSuggestionsData;
    console.log('Using jacob_fenton_suggestions from src/data_store:', suggestions);
  } else {
    suggestions = await loadJSON('_suggestions.json');
  }

  const [history, medications] = await Promise.all([
    loadJSON('_history.json'),
    loadJSON('_medications.json'),
  ]);

  if (onProgress) onProgress(20);

  // Load comprehensive data for barbara_smith
  let comprehensive = null;
  if (patientId === 'barbara_smith') {
    comprehensive = await loadComprehensiveData(patientId, (subProgress) => {
      if (onProgress) onProgress(20 + (subProgress * 0.8));
    });
  }

  if (onProgress) onProgress(100);

  return { suggestions, history, medications, comprehensive };
}

/**
 * Load comprehensive patient data from large JSON file
 */
async function loadComprehensiveData(
  patientId: string,
  onProgress?: (progress: number) => void
): Promise<ComprehensivePatientData | null> {
  try {
    console.log(`Loading comprehensive data for ${patientId}...`);
    const response = await fetch(`/data_store/${patientId}.json`);

    if (!response.ok) {
      console.warn(`Comprehensive data not found for ${patientId}`);
      return null;
    }

    if (onProgress) onProgress(30);
    const data = await response.json();
    console.log(`Loaded ${data.encounters?.length || 0} encounters`);

    if (onProgress) onProgress(60);
    return data;
  } catch (error) {
    console.error(`Failed to load comprehensive data:`, error);
    return null;
  }
}

/**
 * Extract events from comprehensive patient data
 */
function extractComprehensiveData(comprehensive: ComprehensivePatientData) {
  const measurements: any = {
    bloodPressure: [],
    heartRate: [],
    weight: [],
    bmi: [],
    glucose: [],
    ldlCholesterol: [],
    hdlCholesterol: [],
    totalCholesterol: [],
    hba1c: [],
    creatinine: [],
    egfr: [],
    apoB: []
  };

  const labResults: any[] = [];
  const bpMap = new Map<string, { systolic?: number; diastolic?: number }>();

  // Parse all encounters and entries
  for (const encounter of comprehensive.encounters) {
    for (const entry of encounter.entries) {
      const date = entry.timestamp.split('T')[0];

      for (const event of entry.events) {
        if (event.type !== 'measurement') continue;

        const value = parseFloat(event.value || '0');
        if (isNaN(value)) continue;

        // Map LOINC/SNOMED codes to metrics
        switch (event.code) {
          case 'LOINC/8480-6': // Systolic BP
          case 'SNOMED/271649006':
            if (!bpMap.has(entry.timestamp)) bpMap.set(entry.timestamp, {});
            bpMap.get(entry.timestamp)!.systolic = value;
            break;

          case 'LOINC/8462-4': // Diastolic BP
          case 'SNOMED/271650006':
            if (!bpMap.has(entry.timestamp)) bpMap.set(entry.timestamp, {});
            bpMap.get(entry.timestamp)!.diastolic = value;
            break;

          case 'LOINC/8867-4': // Heart Rate
          case 'SNOMED/78564009':
            measurements.heartRate.push({ date, value });
            break;

          case 'LOINC/29463-7': // Weight (convert g to kg)
            measurements.weight.push({ date, value: value / 1000 });
            break;

          case 'LOINC/39156-5': // BMI
            measurements.bmi.push({ date, value });
            break;

          case 'LOINC/2339-0': // Glucose
          case 'LOINC/2345-7':
            measurements.glucose.push({ date, value });
            break;

          case 'LOINC/2089-1': // LDL
          case 'LOINC/13457-7':
            measurements.ldlCholesterol.push({ date, value });
            break;

          case 'LOINC/2085-9': // HDL
            measurements.hdlCholesterol.push({ date, value });
            break;

          case 'LOINC/2093-3': // Total Cholesterol
            measurements.totalCholesterol.push({ date, value });
            break;

          case 'LOINC/4548-4': // HbA1c
          case 'LOINC/17856-6':
            measurements.hba1c.push({ date, value });
            break;

          case 'LOINC/2160-0': // Creatinine
            measurements.creatinine.push({ date, value });
            break;

          case 'LOINC/62238-1': // eGFR
          case 'LOINC/48642-3':
            measurements.egfr.push({ date, value });
            break;
        }
      }
    }
  }

  // Combine BP systolic/diastolic pairs
  for (const [timestamp, bp] of bpMap.entries()) {
    if (bp.systolic && bp.diastolic) {
      const date = timestamp.split('T')[0];
      measurements.bloodPressure.push({
        date,
        systolic: bp.systolic,
        diastolic: bp.diastolic
      });
    }
  }

  // Sort all arrays by date
  for (const key of Object.keys(measurements)) {
    measurements[key].sort((a: any, b: any) =>
      new Date(a.date).getTime() - new Date(b.date).getTime()
    );
  }

  console.log(`Extracted: BP=${measurements.bloodPressure.length}, HR=${measurements.heartRate.length}, glucose=${measurements.glucose.length}`);

  return { measurements, labResults };
}

/**
 * Process medications from JSON, grouping ALL events by RxNorm code into single entries
 */
function processMedications(jsonMeds: any[] | null): any[] {
  if (!jsonMeds) return [];

  // Group all medication events by RxNorm code
  const medicationsByCode = new Map<string, any[]>();

  for (const med of jsonMeds) {
    const code = med.code || 'UNKNOWN';
    if (!medicationsByCode.has(code)) {
      medicationsByCode.set(code, []);
    }
    medicationsByCode.get(code)!.push(med);
  }

  const processedMedications: any[] = [];
  let idCounter = 1;

  // Process each medication group
  for (const [code, events] of medicationsByCode.entries()) {
    // Sort events by timestamp (oldest first)
    events.sort((a: any, b: any) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

    // Separate by event type
    const starts = events.filter((e: any) => e.value === 'start');
    const ends = events.filter((e: any) => e.value === 'end');
    const delivered = events.filter((e: any) => e.value === 'start|end');

    // Get medication name and dosage from first event
    const firstEvent = events[0];
    const medName = firstEvent.name || 'Unknown';
    const dosage = extractFullDosage(medName);

    const datePairs: any[] = [];

    // Process delivered medications (start|end)
    for (const med of delivered) {
      datePairs.push({
        deliveredDate: med.timestamp.split('T')[0],
        status: 'delivered'
      });
    }

    // Pair start and end events
    const paired = new Set<number>();
    for (let i = 0; i < starts.length; i++) {
      const start = starts[i];

      // Find matching end event (same code, after start time)
      const matchingEnd = ends.find((end: any, endIdx: number) => {
        if (paired.has(endIdx)) return false;
        return new Date(end.timestamp) >= new Date(start.timestamp);
      });

      if (matchingEnd) {
        // Paired start/end
        const endIdx = ends.indexOf(matchingEnd);
        paired.add(endIdx);

        datePairs.push({
          prescribedDate: start.timestamp.split('T')[0],
          discontinuedDate: matchingEnd.timestamp.split('T')[0],
          status: 'discontinued'
        });
      } else {
        // Unpaired start (still active)
        datePairs.push({
          prescribedDate: start.timestamp.split('T')[0],
          status: 'active'
        });
      }
    }

    // Determine overall status
    const hasActive = datePairs.some(p => p.status === 'active');
    const hasDelivered = datePairs.some(p => p.status === 'delivered');
    const hasDiscontinued = datePairs.some(p => p.status === 'discontinued');

    let overallStatus: string;
    if (hasActive) {
      overallStatus = 'active';
    } else if (hasDelivered && hasDiscontinued) {
      overallStatus = 'mixed';
    } else if (hasDelivered) {
      overallStatus = 'delivered';
    } else {
      overallStatus = 'discontinued';
    }

    // Sort date pairs by most recent first
    datePairs.sort((a, b) => {
      const dateA = a.deliveredDate || a.prescribedDate || a.discontinuedDate || '';
      const dateB = b.deliveredDate || b.prescribedDate || b.discontinuedDate || '';
      return new Date(dateB).getTime() - new Date(dateA).getTime();
    });

    // Count prescriptions and deliveries
    const totalPrescriptions = datePairs.filter(p => p.prescribedDate).length;
    const totalDeliveries = datePairs.filter(p => p.deliveredDate).length;

    processedMedications.push({
      id: `med-${idCounter++}`,
      name: medName,
      dosage: dosage,
      code: code,
      datePairs: datePairs,
      overallStatus: overallStatus,
      totalPrescriptions: totalPrescriptions,
      totalDeliveries: totalDeliveries
    });
  }

  // Sort medications by overall status (active first, then mixed, then discontinued, then delivered)
  processedMedications.sort((a, b) => {
    const statusOrder: Record<string, number> = { active: 0, mixed: 1, discontinued: 2, delivered: 3 };
    return statusOrder[a.overallStatus] - statusOrder[b.overallStatus];
  });

  return processedMedications;
}

/**
 * Extract full dosage string from medication name
 * E.g., "prednisone 10 MG Oral Tablet" -> "10 MG Oral Tablet"
 */
function extractFullDosage(medName: string): string {
  if (!medName) return 'N/A';

  // Match dosage pattern: number + unit + optional form
  // Examples: "10 MG", "0.01 MG/ML Inhalation Solution", "60 ACTUAT"
  const dosageMatch = medName.match(/\d+(?:\.\d+)?\s*[A-Z]+(?:\/[A-Z]+)?\s*(?:[A-Za-z\s]+)?$/i);

  if (dosageMatch) {
    return dosageMatch[0].trim();
  }

  // If no match, check if there's a number with unit anywhere
  const partialMatch = medName.match(/\d+(?:\.\d+)?\s*(?:MG|G|MCG|ML|ACTUAT|units?)(?:\/(?:ML|DAY|ACTUAT))?/i);
  return partialMatch ? partialMatch[0] : 'N/A';
}

/**
 * Map lab test name to reference range key
 */
function getLabTestKey(testName: string): string | null {
  const normalized = testName.toLowerCase().trim();

  // Direct mappings
  const mappings: Record<string, string> = {
    'hba1c': 'hba1c',
    'hemoglobin a1c': 'hba1c',
    'apob': 'apoB',
    'ldl-c': 'ldlCholesterol',
    'ldl': 'ldlCholesterol',
    'hdl-c': 'hdlCholesterol',
    'hdl': 'hdlCholesterol',
    'total cholesterol': 'totalCholesterol',
    'cholesterol': 'totalCholesterol',
    'triglycerides': 'triglycerides',
    'tsh': 'tsh',
    'free t4': 'freeT4',
    't4': 'freeT4',
    'bun': 'bun',
    'creatinine': 'creatinine',
    'egfr': 'egfr',
    'glucose': 'glucose',
    'vitamin d': 'vitaminD',
    '25-oh vitamin d': 'vitaminD',
  };

  return mappings[normalized] || null;
}

/**
 * Convert JSON timeline to Patient timeline format
 */
function convertTimeline(historyData: any | null): Record<string, any> {
  if (!historyData?.timeline) return {};

  const timeline: Record<string, any> = {};

  for (const entry of historyData.timeline) {
    if (entry.date) {
      timeline[entry.date] = {
        content: entry.event || '',
        tags: (entry.tags || []).map((tag: string) => ({
          label: tag,
          type: 'category',
          icon: 'Activity',
          color: 'blue',
        })),
      };
    }
  }

  return timeline;
}

/**
 * Convert labs from history JSON to LabResult format
 */
function convertLabs(historyData: any | null): any[] {
  if (!historyData?.labs) return [];

  return historyData.labs.map((lab: any, index: number) => {
    const testKey = getLabTestKey(lab.test);
    let referenceRange = 'N/A';

    // Lookup reference range from config
    if (testKey && CLEVELAND_HEARTLAB_RANGES[testKey]) {
      const range = CLEVELAND_HEARTLAB_RANGES[testKey];
      referenceRange = range.displayRange || 'N/A';
    }

    return {
      id: `lab-${index + 1}`,
      name: lab.test,
      value: parseLabValue(lab.value),
      unit: extractUnit(lab.value),
      referenceRange: referenceRange,
      status: lab.interpretation === 'normal' ? 'normal' : 'abnormal',
      date: lab.date,
    };
  });
}

/**
 * Parse numeric value from lab result string
 */
function parseLabValue(valueStr: string): number {
  if (!valueStr) return 0;
  // Extract first number from the string
  const match = valueStr.match(/(\d+\.?\d*)/);
  return match ? parseFloat(match[1]) : 0;
}

/**
 * Extract unit from lab result string
 */
function extractUnit(valueStr: string): string {
  if (!valueStr) return '';
  // Try to extract unit after number (e.g., "5.1%" -> "%", "52 mg/dL" -> "mg/dL")
  const match = valueStr.match(/\d+\.?\d*\s*([a-zA-Z/%]+(?:\/[a-zA-Z]+)?)/);
  return match ? match[1] : '';
}

/**
 * Convert vitals from history JSON to historical data format
 */
function convertVitals(historyData: any | null): any {
  if (!historyData?.vitals) return {};

  const historicalData: any = {};

  for (const vital of historyData.vitals) {
    const date = vital.date;
    const measurement = vital.measurement;
    const value = vital.value;

    if (measurement === 'Heart rate') {
      if (!historicalData.heartRate) historicalData.heartRate = [];
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        historicalData.heartRate.push({ date, value: numValue });
      }
    } else if (measurement === 'Weight') {
      if (!historicalData.weight) historicalData.weight = [];
      // Extract numeric value from string like "185 lb (84 kg)"
      const kgMatch = value.match(/\((\d+\.?\d*)\s*kg\)/);
      if (kgMatch) {
        historicalData.weight.push({ date, value: parseFloat(kgMatch[1]) });
      }
    } else if (measurement === 'Blood pressure') {
      if (!historicalData.bloodPressure) historicalData.bloodPressure = [];
      // Extract systolic/diastolic from string like "112/67 mmHg"
      const bpMatch = value.match(/(\d+)\/(\d+)/);
      if (bpMatch) {
        historicalData.bloodPressure.push({
          date,
          systolic: parseInt(bpMatch[1]),
          diastolic: parseInt(bpMatch[2])
        });
      }
    } else if (measurement === 'BMI') {
      if (!historicalData.bmi) historicalData.bmi = [];
      // Extract numeric value from string like "≈26.2 kg/m2"
      const bmiMatch = value.match(/[\d.]+/);
      if (bmiMatch) {
        const numValue = parseFloat(bmiMatch[0]);
        if (!isNaN(numValue)) {
          historicalData.bmi.push({ date, value: numValue });
        }
      }
    }
  }

  return historicalData;
}

/**
 * Merge supplemental data into Patient object
 */
export function mergeSupplementalData(basePatient: Patient, supplemental: any): Patient {
  const merged = { ...basePatient };

  // Process medications from JSON
  if (supplemental.medications) {
    merged.medications = processMedications(supplemental.medications);
  }

  // Add medical timeline from history JSON
  if (supplemental.history) {
    const convertedTimeline = convertTimeline(supplemental.history);
    if (Object.keys(convertedTimeline).length > 0) {
      merged.medicalTimeline = convertedTimeline;
    }

    // Add labs from history JSON
    const labs = convertLabs(supplemental.history);
    if (labs.length > 0) {
      merged.labResults = labs;
    }

    // Add vitals from history JSON
    const vitals = convertVitals(supplemental.history);
    if (Object.keys(vitals).length > 0) {
      merged.historicalData = {
        ...(merged.historicalData || {}),
        ...vitals
      };

      // Update cardiovascular analysis with real vital values
      if (vitals.bloodPressure && vitals.bloodPressure.length > 0) {
        const latestBP = vitals.bloodPressure[vitals.bloodPressure.length - 1];
        if (latestBP.systolic) merged.healthMetrics.cardiovascularAnalysis.systolic = latestBP.systolic;
        if (latestBP.diastolic) merged.healthMetrics.cardiovascularAnalysis.diastolic = latestBP.diastolic;
      }

      if (vitals.heartRate && vitals.heartRate.length > 0) {
        const latestHR = vitals.heartRate[vitals.heartRate.length - 1];
        merged.healthMetrics.cardiovascularAnalysis.heartRate = latestHR.value;
      }

      if (vitals.bmi && vitals.bmi.length > 0) {
        const latestBMI = vitals.bmi[vitals.bmi.length - 1];
        merged.healthMetrics.obesityAnalysis.bmi = latestBMI.value;
      }

      // Set lastVisit to the most recent vitals date
      const allVitalDates: string[] = [];
      if (vitals.bloodPressure) allVitalDates.push(...vitals.bloodPressure.map((v: any) => v.date));
      if (vitals.heartRate) allVitalDates.push(...vitals.heartRate.map((v: any) => v.date));
      if (vitals.weight) allVitalDates.push(...vitals.weight.map((v: any) => v.date));
      if (vitals.bmi) allVitalDates.push(...vitals.bmi.map((v: any) => v.date));

      if (allVitalDates.length > 0) {
        // Sort dates and get the most recent
        allVitalDates.sort();
        merged.lastVisit = allVitalDates[allVitalDates.length - 1];
      }
    }

    // Add procedures from history JSON
    if (supplemental.history.procedures) {
      merged.procedures = supplemental.history.procedures;
    }
  }

  // Store supplemental data for AI suggestions component
  if (supplemental.suggestions) {
    (merged as any).aiSuggestions = supplemental.suggestions;
  }

  // Process comprehensive data
  if (supplemental.comprehensive) {
    console.log('Processing comprehensive data...');
    const extracted = extractComprehensiveData(supplemental.comprehensive);

    // Merge measurements into historicalData
    merged.historicalData = {
      ...(merged.historicalData || {}),
      ...extracted.measurements
    };

    // Update healthMetrics with latest values
    const { measurements } = extracted;

    if (measurements.bloodPressure.length > 0) {
      const latest = measurements.bloodPressure[measurements.bloodPressure.length - 1];
      merged.healthMetrics.cardiovascularAnalysis.systolic = latest.systolic;
      merged.healthMetrics.cardiovascularAnalysis.diastolic = latest.diastolic;
    }

    if (measurements.heartRate.length > 0) {
      const latest = measurements.heartRate[measurements.heartRate.length - 1];
      merged.healthMetrics.cardiovascularAnalysis.heartRate = latest.value;
    }

    if (measurements.glucose.length > 0) {
      const latest = measurements.glucose[measurements.glucose.length - 1];
      merged.healthMetrics.metabolicAnalysis.glucose = latest.value;
    }

    if (measurements.hba1c.length > 0) {
      const latest = measurements.hba1c[measurements.hba1c.length - 1];
      merged.healthMetrics.metabolicAnalysis.a1c = latest.value;
    }

    if (measurements.creatinine.length > 0) {
      const latest = measurements.creatinine[measurements.creatinine.length - 1];
      merged.healthMetrics.metabolicAnalysis.creatinine = latest.value;
    }

    if (measurements.egfr.length > 0) {
      const latest = measurements.egfr[measurements.egfr.length - 1];
      merged.healthMetrics.metabolicAnalysis.egfr = latest.value;
    }

    if (measurements.ldlCholesterol.length > 0) {
      const latest = measurements.ldlCholesterol[measurements.ldlCholesterol.length - 1];
      merged.healthMetrics.cholesterolAnalysis.ldl = latest.value;
    }

    if (measurements.hdlCholesterol.length > 0) {
      const latest = measurements.hdlCholesterol[measurements.hdlCholesterol.length - 1];
      merged.healthMetrics.cholesterolAnalysis.hdl = latest.value;
    }

    if (measurements.totalCholesterol.length > 0) {
      const latest = measurements.totalCholesterol[measurements.totalCholesterol.length - 1];
      merged.healthMetrics.cholesterolAnalysis.total = latest.value;
    }

    if (measurements.bmi.length > 0) {
      const latest = measurements.bmi[measurements.bmi.length - 1];
      merged.healthMetrics.obesityAnalysis.bmi = latest.value;
    }

    console.log('Comprehensive data merged successfully');
  }

  return merged;
}
